import type { QuestionImageInput } from '../domain/entities/content.entity';

/**
 * A tradução entre o que o **backend guarda** e o que o **editor manipula**.
 *
 * O back guarda imagem como token de texto, em duas gramáticas (espelha
 * `content/domain/content-types/image-tokens.ts` no monolith — mudar um lado
 * exige mudar o outro):
 *
 * 1. `{{img:<fileId>}}` — imagem já salva. É o que está no banco.
 * 2. `[](imagens/nome.png)` — imagem anexada agora, que ainda não subiu. Mesma
 *    sintaxe do CSV de importação. Morre no upload, virando (1).
 *
 * Nenhum dos dois é tag HTML, e isso é o ponto: o allowlist de
 * `sanitizeRichText` no back não tem `img`, e um token de texto ainda conta como
 * conteúdo visível para `richTextIsEmpty()` (é o que faz "questão só de imagem"
 * ser um corpo válido).
 *
 * O **editor** manipula nó, não texto (ver `tiptap/question-image.ts`). Então a
 * conversão acontece nas duas bordas, e só aqui:
 *
 * ```
 * banco  --tokensToEditorHtml-->  editor  --editorHtmlToTokens-->  banco
 * ```
 */

/** `[](imagens/nome.png)` — o token da imagem anexada. Captura o nome. */
const SOURCE_TOKEN = /\[\]\(imagens\/([^)]+)\)/g;

/** `{{img:<fileId>}}` — o token da imagem já salva. Captura o `fileId`. */
const STORED_TOKEN = /\{\{img:([^}]+)\}\}/g;

export function sourceImageToken(filename: string): string {
  return `[](imagens/${filename})`;
}

/**
 * Texto do banco → HTML que o editor entende (cada token vira um `<img>` do nó
 * `questionImage`, com a URL de exibição resolvida).
 *
 * `fileId` sem URL no mapa (arquivo apagado, ou tela que não recebeu o mapa)
 * continua virando nó: perde a miniatura, não a identidade — o `alt` diz qual
 * era, e a serialização de volta devolve o token intacto. Sumir com a imagem
 * seria apagar conteúdo da questão por causa de uma URL.
 */
export function tokensToEditorHtml(
  text: string,
  urlByFileId: Record<string, string>,
): string {
  return text
    .replace(STORED_TOKEN, (_whole, fileId: string) =>
      imageNodeHtml({
        'data-file-id': fileId,
        src: urlByFileId[fileId],
        alt: `imagem ${fileId.slice(0, 8)}`,
      }),
    )
    .replace(SOURCE_TOKEN, (_whole, filename: string) =>
      imageNodeHtml({ 'data-filename': filename, alt: filename }),
    );
}

/**
 * HTML do editor → texto do banco. É o inverso exato do de cima.
 *
 * Usa `DOMParser` em vez de regex sobre `<img …>` de propósito: a ordem dos
 * atributos que o TipTap emite não é contrato, e um regex que assume ordem
 * quebra em silêncio — devolvendo a tag inteira para o back, que a apagaria, e
 * a imagem sumiria da questão sem erro nenhum.
 */
export function editorHtmlToTokens(html: string): string {
  const doc = new DOMParser().parseFromString(
    `<body>${html}</body>`,
    'text/html',
  );
  for (const img of [
    ...doc.body.querySelectorAll('img[data-question-image]'),
  ]) {
    img.replaceWith(doc.createTextNode(tokenForNode(img)));
  }
  return doc.body.innerHTML;
}

/**
 * Os nomes de arquivo que o documento **realmente cita**, entre os anexados.
 *
 * É o que vai no payload — e é por isso que não existe mais "imagem anexada e
 * não usada": remover o nó tira a imagem do upload junto. Antes, o descompasso
 * era estado possível e a UI tinha de avisar.
 */
export function attachedFilenames(htmls: readonly string[]): Set<string> {
  const found = new Set<string>();
  for (const html of htmls) {
    const doc = new DOMParser().parseFromString(
      `<body>${html}</body>`,
      'text/html',
    );
    for (const img of doc.body.querySelectorAll('img[data-filename]')) {
      const filename = img.getAttribute('data-filename');
      if (filename) found.add(filename);
    }
  }
  return found;
}

function tokenForNode(img: Element): string {
  const fileId = img.getAttribute('data-file-id');
  if (fileId) return `{{img:${fileId}}}`;
  const filename = img.getAttribute('data-filename');
  return filename ? sourceImageToken(filename) : '';
}

function imageNodeHtml(attrs: Record<string, string | undefined>): string {
  const rendered = Object.entries({ ...attrs, 'data-question-image': '' })
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}="${escapeAttribute(value!)}"`)
    .join(' ');
  return `<img ${rendered}>`;
}

/**
 * A URL assinada do storage vem cheia de `&` (`&X-Goog-Signature=…`); sem
 * escapar, o `&` cru é HTML inválido e alguns parsers comem o resto dos
 * parâmetros.
 */
function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Abre o seletor de arquivo do sistema sem depender de um `<input>` no DOM —
 * é o que permite o botão de imagem viver na barra do editor, onde o cursor
 * está.
 *
 * `cancel` cobre "fechou o diálogo sem escolher". Onde o evento não existe, a
 * promise simplesmente não resolve — e não resolver é inofensivo aqui: nada é
 * inserido e nenhum estado fica pendurado.
 */
export function openImageFilePicker(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    // Anexado ao DOM, e não solto: input destacado abre o diálogo no Chrome e
    // no Firefox, mas não em todo browser — e um `click()` que não abre nada é
    // um botão que simplesmente não faz efeito.
    document.body.appendChild(input);

    const settle = (file: File | null) => {
      input.remove();
      resolve(file);
    };
    input.addEventListener('change', () => settle(input.files?.[0] ?? null));
    input.addEventListener('cancel', () => settle(null));
    input.click();
  });
}

/**
 * Data URI a partir do que `preparedToBase64` devolveu. Ele já devolve data URI
 * para bytes que vieram da memória (o caso do anexo manual); o prefixo cobre o
 * base64 cru, para o chamador não ter de saber de qual caminho veio.
 *
 * **Data URI e não `objectURL`**: a sanitização (DOMPurify) permite `data:` em
 * `<img>` mas bloqueia `blob:`, e a aba Preview mostraria uma imagem vazia sem
 * nada indicando o motivo. Coberto por teste.
 */
export function toDataUri(base64: string, mime: string): string {
  return base64.startsWith('data:') ? base64 : `data:${mime};base64,${base64}`;
}

/** Uma imagem anexada nesta edição: os bytes para subir, e como exibi-los. */
export interface AttachedImage {
  filename: string;
  /** O que vai no payload (data URI ou base64 cru — o back aceita os dois). */
  base64: string;
  /** `src` do nó dentro do editor. Data URI, pelo motivo em `toDataUri`. */
  previewUrl: string;
}

/**
 * `AttachedImage[]` → payload, **filtrado pelo que o documento cita**. Imagem
 * cujo nó foi apagado não sobe: sem esse filtro seria upload órfão, que o back
 * recusa ("arquivo sem token").
 */
export function toImagePayload(
  images: readonly AttachedImage[],
  used: ReadonlySet<string>,
): QuestionImageInput[] {
  return images
    .filter((image) => used.has(image.filename))
    .map(({ filename, base64 }) => ({ filename, base64 }));
}
